import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Stack } from 'react-bootstrap';
import { BrowserView, MobileView, isMobile } from 'react-device-detect';
import { useErrorBoundary } from 'react-error-boundary';
import { useQueryClient } from '@tanstack/react-query';
import Select, { MultiValue } from 'react-select';
import { format } from 'date-fns';
import { useJobWorkerShiftContext } from '@company/state/jobWorkerShiftContext';
import RefreshButton from '@company/components/RefreshButton/RefreshButton';
import { useActivityContext } from '@company/state/activityContext';
import { useJobShiftContext } from '@company/state/jobShiftContext';
import { WorkerModal } from '@company/components/WorkerModal';
import { useChatContext } from '@company/state/chatContext';
import { useJobContext } from '@company/state/jobContext';
import * as jobApi from '@company/services/job/api';
import {
  useShiftMissingActivitiesNoticedMutation,
  useActivityTrackerMissingHoursNoticedMutation,
} from '@company/hooks/useShiftMutations';
import ThFilterBadge from '@components/elements/ThFilterBadge';
import { ModalConfirm, ThError } from '@components/elements';
import ThLoading from '@components/elements/ThLoading';
import { ActivityTrackerView, ActivityFilters } from '@th-types/jobs.type';
import { ActivityDetail } from '@th-types/daily-activity.type';
import IModalConfirmModel from '@th-types/confirm-model.type';
import { JobAssignment } from '@th-types/job.detail.type';
import {
  activityFilterOptions,
  getFilteredData,
  getTotalActivities,
} from '@services/activity-tracker/activityTrackerService';
import DATE_FORMATS from '@constants/dateFormat';
import { approveTimeSheet } from '@utils/JobActivityUtils';
import { isShiftPayable } from '@utils/ShiftsUtils';
import { UTC_TIMEZONE } from '@utils/DateUtils';
import useAlert from '@hooks/useAlert';
import ActivityTrackerMobile from './ActivityTrackerMobile';
import ActivityTrackerDesktop from './ActivityTrackerDesktop';
import './styles.scss';

interface Params {
  setActivitiesCount: (value: number) => void;
}

function ActivityTracker({ setActivitiesCount }: Params) {
  const { showBoundary } = useErrorBoundary();
  const { showSuccessAlert, showWarningAlert, alertMessage } = useAlert();
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const { refetch: refetchShifts } = useJobShiftContext();
  const [hasFilteredActivities, setHasFilteredActivities] = useState(false);
  const [clickedJobAssignment, setClickedJobAssignment] = useState<
    JobAssignment | undefined
  >();
  const { chatPerson } = useChatContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfirmModel, setModalConfirmModel] =
    useState<IModalConfirmModel>();

  const queryClient = useQueryClient();

  const { job } = useJobContext();
  const {
    error,
    viewType,
    upcoming,
    isLoading,
    activities,
    isRefetching,
    isLoadingAction,
    activitiesFilter,
    activitiesQueryKey,
    groupedByDateActivities,
    groupedByWorkerActivities,
    refetch,
    setUpcoming,
    setViewType,
    isShiftUnPayable,
    setIsLoadingAction,
    setActivitiesFilter,
    enableDataFetching,
  } = useActivityContext();
  const { setStatusFilter, setSelectedWorker, setSelectedShifts } =
    useJobShiftContext();

  const { setIsJobWorkerSideModalOpen } = useJobWorkerShiftContext();

  // Enable data fetching when component mounts
  useEffect(() => {
    enableDataFetching();
  }, [enableDataFetching]);

  const handleActivitiesCount = useCallback(async () => {
    if (activities) {
      setActivitiesCount(activities?.length ?? 0);
    }
  }, [activities, setActivitiesCount]);

  useEffect(() => {
    handleActivitiesCount();
  }, [activities, handleActivitiesCount]);

  useEffect(() => {
    if (setIsLoadingAction) {
      setIsLoadingAction(isRefetching);
    }
  }, [isRefetching, setIsLoadingAction]);

  useEffect(() => {
    if (activities) {
      setHasFilteredActivities(
        getFilteredData(
          activities,
          activitiesFilter,
          upcoming,
          job?.timezone ?? UTC_TIMEZONE
        )?.length > 0
      );
    }
  }, [activities, activitiesFilter, job?.timezone, upcoming]);

  const mutationMissingActivitiesNoticed =
    useShiftMissingActivitiesNoticedMutation({
      queryClient,
      activitiesQueryKey,
    });

  const mutationMissingHoursNoticed =
    useActivityTrackerMissingHoursNoticedMutation({
      queryClient,
      activitiesQueryKey,
    });

  const handleDateButton = () => setViewType(ActivityTrackerView.DATE);
  const handleWorkerButton = () => setViewType(ActivityTrackerView.WORKER);
  const toggleUpcomingShifts = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpcoming(e.target.checked);
    if (e.target.checked) {
      setActivitiesFilter([...activitiesFilter, ActivityFilters.UPCOMING]);
    } else {
      setActivitiesFilter(
        activitiesFilter.filter((x) => x !== ActivityFilters.UPCOMING)
      );
    }
  };

  const activityKey = (value: string) => {
    return value as keyof typeof ActivityFilters;
  };

  const noticedMissingActivitiesRefreshQueryData = async (
    shiftPositionId: number
  ) => {
    try {
      setIsLoadingAction(true);
      mutationMissingActivitiesNoticed.mutate(shiftPositionId);
    } catch (err) {
      showWarningAlert(
        'An error occurred trying to dismiss the missing activity notification.'
      );
      console.log(
        'An error occurred trying to set missing activities as noticed',
        err
      );
    } finally {
      setIsLoadingAction(false);
    }
  };

  const noticedMissingHoursRefreshQueryData = async (
    shiftPositionId: number
  ) => {
    try {
      setIsLoadingAction(true);
      mutationMissingHoursNoticed.mutate(shiftPositionId);
    } catch (err) {
      showWarningAlert(
        'An error occurred trying to dismiss the missing hours notification.'
      );
      console.log(
        'An error occurred trying to set missing hours as noticed',
        err
      );
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleFilterSelection = async (filter: ActivityFilters) => {
    if (activitiesFilter.includes(filter as string)) {
      setActivitiesFilter(
        activitiesFilter.filter((x) => x !== (filter as string))
      );
      if (filter === ActivityFilters.UPCOMING) {
        setUpcoming(false);
      }
    } else {
      setActivitiesFilter([...activitiesFilter, filter as string]);
      if (filter === ActivityFilters.UPCOMING) {
        setUpcoming(true);
      }
    }
  };

  const openWorkerSideModal = (jobAssignment: JobAssignment) => {
    setClickedJobAssignment(jobAssignment);
    setShowWorkerProfile(true);
    refetchShifts();
    setSelectedShifts([]);
    setIsJobWorkerSideModalOpen(true);
  };

  const handleCloseWorkerProfile = () => {
    setShowWorkerProfile(false);
    setIsJobWorkerSideModalOpen(false);
    setClickedJobAssignment(undefined);
    setStatusFilter([]);
    setSelectedWorker(undefined);
  };

  const handleFilterSelectionMobile = (
    selectedOptions: MultiValue<{
      value: string;
      label: string;
    }>
  ) => {
    setActivitiesFilter(selectedOptions.map((opt) => activityKey(opt.value)));
  };

  if (error) {
    showBoundary(error); // pass the error as string to the error boundary
    if (error.response?.status) {
      return <>No data found</>;
    }
    return <ThError message={error.message} />;
  }

  const markAsPaid = async (activityList: ActivityDetail[]) => {
    try {
      setIsLoadingAction(true);
      let successMessage = '';
      const shiftIds = activityList.map((x) => x.shiftPosition.id);
      const result = await jobApi.markShiftsPaid(
        activityList[0].shiftPosition.jobId,
        shiftIds
      );
      successMessage = result.message;
      if (result.success) {
        showSuccessAlert(successMessage);
        refetch();
      }
    } catch (err) {
      console.log(`Occurred an error trying to cancel shift`, err);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const markAsUnpaid = async (activityList: ActivityDetail[]) => {
    try {
      setIsLoadingAction(true);
      let successMessage = '';
      const shiftIds = activityList.map((shift) => shift.shiftPosition.id);
      const result = await jobApi.markShiftsUnPaid(
        activityList[0].shiftPosition.jobId,
        shiftIds
      );
      successMessage = result.message;
      if (result.success) {
        showSuccessAlert(successMessage);
        refetch();
      }
    } catch (err) {
      console.log(`Occurred an error trying to cancel shift`, err);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const executeApproveTimeSheet = async (activityDetail: ActivityDetail) => {
    approveTimeSheet(
      activityDetail,
      setIsLoadingAction,
      showSuccessAlert,
      showWarningAlert,
      refetch
    );
  };

  const handleMarkPaid = (id: number, listActivities: ActivityDetail[]) => {
    const activity = listActivities.find((a) => a.shiftPosition.id === id);
    if (activity && job && isShiftPayable(job, activity)) {
      markAsPaid(activity ? [activity] : []);
    }
  };

  const handleMarkAllPaid = (listActivities: ActivityDetail[]) => {
    if (job) {
      markAsPaid(
        listActivities.filter((activity) => isShiftPayable(job, activity))
      );
    }
  };

  const handleMarkUnPaid = (id: number, listActivities: ActivityDetail[]) => {
    const activity = listActivities.find((a) => a.shiftPosition.id === id);
    if (activity && isShiftUnPayable(activity)) {
      markAsUnpaid(activity ? [activity] : []);
    }
  };

  const handleMarkAllUnPaid = (listActivities: ActivityDetail[]) => {
    markAsUnpaid(
      listActivities.filter((activity) => isShiftUnPayable(activity))
    );
  };

  const getMarkAsPaidConfirmationMessage = (
    all: boolean,
    filteredActivities: ActivityDetail[]
  ) => {
    if (all) {
      return (
        <p>
          This action will mark <b>ALL</b> shifts available to be paid, that are
          related to{' '}
          {viewType === ActivityTrackerView.WORKER
            ? `the worker ${filteredActivities[0].shiftPosition.jobAssignment?.worker.fullName}`
            : `the date ${format(
                new Date(filteredActivities[0].shiftPosition.date),
                DATE_FORMATS.DATE_FORMAT_SHORT
              )},`}{' '}
          as paid
        </p>
      );
    }
    return (
      <p>
        This action will mark <b>this shift as paid</b>
      </p>
    );
  };

  const getModalConfirmData = (
    groupedById: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all: boolean = false
  ) => {
    const unpaidActivities = all
      ? filteredActivities.filter((x) => !x.shiftPosition.isPaid)
      : filteredActivities.find((a) => a.shiftPosition.id === id);
    if (unpaidActivities) {
      setModalConfirmModel({
        message: (
          <div className="text-center">
            {getMarkAsPaidConfirmationMessage(all, filteredActivities)}
          </div>
        ),
        titleMessage: (
          <div>
            <span>Are you sure?</span>
          </div>
        ),
        confirmFunction: all
          ? () => handleMarkAllPaid(filteredActivities)
          : () => handleMarkPaid(id, filteredActivities),
        show: showConfirmModal,
        styleClass: 'modal-confirm-style',
      });
    }
  };

  const getModalConfirmDataUnpaid = (
    groupedById: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all: boolean = false
  ) => {
    const paidActivities = all
      ? filteredActivities.filter((x) => x.shiftPosition.isPaid)
      : filteredActivities.find((a) => a.shiftPosition.id === id);
    if (paidActivities) {
      setModalConfirmModel({
        message: (
          <div className="text-center">
            <p>
              This action will mark <b>this shift as unpaid</b>
            </p>
          </div>
        ),
        titleMessage: (
          <div>
            <span>Are you sure?</span>
          </div>
        ),
        confirmFunction: all
          ? () => handleMarkAllUnPaid(filteredActivities)
          : () => handleMarkUnPaid(id, filteredActivities),
        show: showConfirmModal,
        styleClass: 'modal-confirm-style',
      });
    }
  };

  const handleConfirmMarkPaid = (
    groupedById: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all: boolean = false
  ) => {
    if (filteredActivities.length === 0 && !all) {
      showWarningAlert('There is no shifts to mark as paid');
      return;
    }
    setShowConfirmModal(true);
    getModalConfirmData(groupedById, id, filteredActivities, all);
  };

  const handleConfirmMarkUnPaid = (
    groupedById: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all: boolean = false
  ) => {
    if (filteredActivities.length === 0 && !all) {
      showWarningAlert('There is no shifts to mark as unpaid');
      return;
    }
    setShowConfirmModal(true);
    getModalConfirmDataUnpaid(groupedById, id, filteredActivities, all);
  };

  const showNoActivitiesMessage = (message?: string) => {
    const messageToShow =
      message ||
      'Create a schedule, and once a worker accepts a shift, they’ll appear here for you to easily track their day of shifts!';
    return (
      <div className="mt-5 d-flex justify-content-center">
        <p>{messageToShow}</p>
      </div>
    );
  };

  const showNoFilteredActivitiesMessage = () => {
    return showNoActivitiesMessage(
      'It’s all clear, reset your quick filters to see everything'
    );
  };

  const getGroupedActivities = () => {
    return viewType === ActivityTrackerView.DATE
      ? groupedByDateActivities
      : groupedByWorkerActivities;
  };

  const showActivitiesList = () => {
    if (activities?.length === 0) {
      return showNoActivitiesMessage();
    }
    const groupedActivities = getGroupedActivities();
    if (!hasFilteredActivities) {
      return showNoFilteredActivitiesMessage();
    }
    return groupedActivities.map(({ groupedBy, groupedByActivities }) => (
      <div key={groupedBy}>
        <MobileView>
          <ActivityTrackerMobile
            key={groupedBy}
            groupedBy={groupedBy}
            activities={groupedByActivities}
            viewType={viewType}
            filters={activitiesFilter}
            approveTimeSheet={executeApproveTimeSheet}
            handleConfirmMarkPaid={handleConfirmMarkPaid}
            handleConfirmMarkUnPaid={handleConfirmMarkUnPaid}
            openWorkerSideModal={openWorkerSideModal}
          />
        </MobileView>
        <BrowserView>
          <ActivityTrackerDesktop
            key={groupedBy}
            groupedBy={groupedBy}
            activities={groupedByActivities}
            viewType={viewType}
            filters={activitiesFilter}
            handleConfirmMarkPaid={handleConfirmMarkPaid}
            handleConfirmMarkUnPaid={handleConfirmMarkUnPaid}
            approveTimeSheet={executeApproveTimeSheet}
            openWorkerSideModal={openWorkerSideModal}
            noticedMissingActivitiesRefreshQueryData={
              noticedMissingActivitiesRefreshQueryData
            }
            noticedMissingHoursRefreshQueryData={
              noticedMissingHoursRefreshQueryData
            }
          />
        </BrowserView>
      </div>
    ));
  };

  return (
    <>
      {(isLoading || isLoadingAction) && <ThLoading />}
      <Row className="justify-content-start align-items-center gx-0">
        <Col xs={12} md={2} className="m-0 p-0">
          <Button
            variant={
              viewType === ActivityTrackerView.DATE ? 'primary' : 'outline-dark'
            }
            className="w-50"
            onClick={handleDateButton}
          >
            DATE
          </Button>
          <Button
            variant={
              viewType === ActivityTrackerView.WORKER
                ? 'primary'
                : 'outline-dark'
            }
            className="w-50"
            onClick={handleWorkerButton}
          >
            WORKER
          </Button>
        </Col>
        <Col
          xs={10}
          md={2}
          className={`d-flex ${isMobile && 'justify-content-between fw-bold'}`}
        >
          <Form.Label className={`fw-bold ${isMobile ? 'pb-2 pt-2' : 'ms-3'}`}>
            SHOW UPCOMING SHIFTS
          </Form.Label>
          <Form.Check
            className={`ps-1 ${isMobile && 'pt-2'}`}
            type="switch"
            id="upcoming-shifts-switch"
            checked={upcoming}
            onChange={toggleUpcomingShifts}
            aria-label="Show Upcoming Shifts"
          />
        </Col>
        <Col
          xs={2}
          md={1}
          className="d-flex justify-content-center align-items-center"
        >
          <RefreshButton
            isSmall
            refreshFunction={refetch}
            className={`${isMobile ? 'mt-1' : ''}`}
          />
        </Col>
        <Col xs={12} md={7}>
          <MobileView>
            <Form.Label className="fw-bold mb-0">QUICK FILTERS</Form.Label>
            <Select
              isMulti
              options={activityFilterOptions}
              placeholder="Select filters"
              isSearchable={false}
              className="mb-3"
              onChange={handleFilterSelectionMobile}
              styles={{
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: 'var(--black)',
                }),
                indicatorSeparator: (provided) => ({
                  ...provided,
                  display: 'none',
                }),
                control: (provided) => ({
                  ...provided,
                  background: 'var(--light)',
                }),
              }}
            />
          </MobileView>
          <BrowserView>
            <Stack direction="horizontal" className="d-flex">
              <Form.Label className="fw-bold ps-2 pe-2">
                QUICK FILTERS
              </Form.Label>
              {activityFilterOptions.map((filter) => (
                <ThFilterBadge
                  key={filter.value}
                  label={filter.label}
                  value={filter.value}
                  aria-label={`Filter by ${filter.label}`}
                  total={getTotalActivities(
                    filter.value,
                    activities,
                    job?.timezone || UTC_TIMEZONE
                  )}
                  selected={activitiesFilter?.includes(
                    filter.value.toLowerCase()
                  )}
                  onClick={() => handleFilterSelection(filter.value)}
                />
              ))}
              {activitiesFilter.length > 0 && (
                <Button
                  size="sm"
                  className="ms-auto text-capitalize"
                  variant="light"
                  onClick={() => setActivitiesFilter([])}
                >
                  Reset Quick filters
                </Button>
              )}
            </Stack>
          </BrowserView>
        </Col>
      </Row>
      <Row>{showActivitiesList()}</Row>
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
      {showConfirmModal && modalConfirmModel && (
        <ModalConfirm
          modalConfirmModel={modalConfirmModel}
          setShowModal={setShowConfirmModal}
        />
      )}
      {showWorkerProfile && clickedJobAssignment && chatPerson === null && (
        <WorkerModal
          workerId={clickedJobAssignment.worker.id}
          jobAssignmentId={clickedJobAssignment.id}
          jobId={clickedJobAssignment.job.id}
          show={showWorkerProfile && !!clickedJobAssignment}
          onClose={handleCloseWorkerProfile}
        />
      )}
    </>
  );
}

export default ActivityTracker;
